import React from 'react';
import { Box, Flex } from 'components/layout';
import { colors } from 'theming/foundations/colors';
import { Button } from 'components/form-elements';
import { Text } from 'components/typography';
import { useModal } from 'store/modals';
import * as Icons from 'components/icons';
import { useLocationAccess } from './use-location-access';

/**
 * Informational pop-up shown after login that requests permission to access the
 * device location.
 *
 * - "Enable location" opts the user in and requests the browser's native
 *   permission prompt, then caches the coordinates so they can auto-populate the
 *   GPS Coordinates field in document headers.
 * - "Not now" simply dismisses the dialog. The app remains fully usable without
 *   location access (backward compatible).
 */
export function LocationPermissionDialog() {
  const { closeModal } = useModal();
  const { enableLocationAccess } = useLocationAccess();
  const [isEnabling, setIsEnabling] = React.useState(false);

  const onEnable = React.useCallback(async () => {
    setIsEnabling(true);
    try {
      await enableLocationAccess();
    } finally {
      setIsEnabling(false);
      closeModal();
    }
  }, [closeModal, enableLocationAccess]);

  return (
    <Box
      sx={{
        p: 6,
        pb: 0,
        minWidth: 280,
        maxWidth: 460,
      }}
    >
      <Flex
        sx={{
          flexDirection: 'column',
          alignItems: 'center',
          textAlign: 'center',
          gap: 4,
        }}
      >
        <Box
          sx={{
            display: 'inline-flex',
            p: 3,
            borderRadius: 'rounded',
            backgroundColor: 'tertiary1.50',
            color: 'tertiary1.600',
          }}
        >
          <Icons.Gps color="currentColor" />
        </Box>

        <Text
          as="h2"
          sx={{ fontSize: '2xl', fontWeight: 500, color: 'secondary.900' }}
          intlId="location.permission.title"
        />

        <Text
          as="p"
          sx={{ color: 'grey.500', lineHeight: 1.5 }}
          intlId="location.permission.description"
        />
      </Flex>

      <Flex
        flexDirection="row"
        alignItems="center"
        sx={{
          borderTop: `2px solid ${colors.grey['300']}`,
          '& > *': {
            flexGrow: 1,
          },
          mt: 5,
        }}
      >
        <Button
          onClick={closeModal}
          intlId="location.permission.dismiss"
          variant="text"
          sx={{ color: 'grey.500', outline: 'none', py: 4 }}
        />
        <Button
          disabled={isEnabling}
          onClick={onEnable}
          intlId="location.permission.enable"
          variant="text"
          sx={{
            py: 4,
            color: 'tertiary1.600',
            outline: 'none',
          }}
        />
      </Flex>
    </Box>
  );
}

